/* @jsx h */
import { defineComponent, onMounted, onUnmounted, ref, Ref, h } from "vue";
import { FloorPlanSelection, TablesFloorplanProps } from "./types";

export const FloorPlanOverylay = defineComponent<
  TablesFloorplanProps & {
    selection: Ref<FloorPlanSelection>;
    gridRef: Ref<HTMLElement | null>;
  }
>({
  name: "FloorPlanOverylay",
  props: [
    "tables",
    "floorPlan",
    "selection",
    "gridRef",
    "onFloorPlanChange",
    "onPayment",
    "isEditable",
    "isLoading",
  ],
  setup(props) {
    const trackers = ref<
      { left: number; top: number; width: number; height: number }[]
    >([]);

    const updateTrackers = () => {
      const area = props.selection.value.area;
      const grid = props.gridRef.value;
      if (!area || !grid) {
        trackers.value = [];
        return;
      }

      // Highlight the full 2D rectangle of cells
      const rowMin = Math.min(area.start.row, area.end.row);
      const rowMax = Math.max(area.start.row, area.end.row);
      const colMin = Math.min(area.start.col, area.end.col);
      const colMax = Math.max(area.start.col, area.end.col);

      // Get bounding rect from corner cells
      const topLeftCell = grid.querySelector(
        `[data-row='${rowMin}'][data-col='${colMin}']`,
      ) as HTMLElement | null;
      const bottomRightCell = grid.querySelector(
        `[data-row='${rowMax}'][data-col='${colMax}']`,
      ) as HTMLElement | null;

      if (!topLeftCell || !bottomRightCell) {
        trackers.value = [];
        return;
      }

      const tlRect = topLeftCell.getBoundingClientRect();
      const brRect = bottomRightCell.getBoundingClientRect();

      trackers.value = [
        {
          left: tlRect.left,
          top: tlRect.top,
          width: brRect.right - tlRect.left,
          height: brRect.bottom - tlRect.top,
        },
      ];
    };

    let handle: number;
    const tick = () => {
      updateTrackers();
      handle = requestAnimationFrame(tick);
    };

    onMounted(() => {
      updateTrackers();
      handle = requestAnimationFrame(tick);
      tick();
    });

    onUnmounted(() => {
      cancelAnimationFrame(handle);
    });

    return () => (
      <>
        {trackers.value.map((t, i) => (
          <div
            key={i}
            class="absolute select-none bg-[var(--floor-plan-selection-background)] border border-[var(--floor-plan-selection-border)]"
            style={{
              left: `${t.left}px`,
              top: `${t.top}px`,
              width: `${t.width}px`,
              height: `${t.height}px`,
              pointerEvents: "none",
            }}
          />
        ))}
      </>
    );
  },
});
